import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Tooltip,
  TextField,
  TablePagination,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Grid,
  Badge,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Search as SearchIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Image as ImageIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Layers as LayersIcon,
} from "@mui/icons-material";
import LeftNavigationBar from "../../../navbars/LeftNavigationBar";
import { useCookies } from "react-cookie";
import { resetSignIn, userVerify } from "../../../redux/slices/user/Signin";
import { useNavigate } from "react-router-dom";
import {
  deleteDegreeProgram,
  fetchDegreeProgramData,
} from "../../../redux/slices/mca/degreeProgram/degreeProgram";

const DegreeProgramControl = () => {
  const dispatch = useDispatch();
  const degreeProgramData =
    useSelector((state) => state.degreeProgram.degreeProgramData) || [];
  const navigate = useNavigate();
  const [deleteId, setDeleteId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [cookies] = useCookies(["token"]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    if (!cookies.token) {
      dispatch(resetSignIn());
      navigate("/");
    } else {
      dispatch(userVerify({ token: cookies.token }));
    }
  }, [cookies, dispatch, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(fetchDegreeProgramData());
        setLoading(false);
      } catch (error) {
        console.error("Error fetching degree programs:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch]);

  const handleEdit = (id) => {
    navigate(`/Degree_Program-edit/${id}`);
  };

  const handleView = (id) => {
    navigate(`/Degree_Program-view/${id}`);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    dispatch(
      deleteDegreeProgram({ token: cookies.token, degreeProgramId: deleteId })
    )
      .then(() => {
        dispatch(fetchDegreeProgramData());
        setConfirmDialogOpen(false);
      })
      .catch((error) => {
        console.error("Error deleting data:", error);
      });
  };

  const handleCloseDialog = () => {
    setDeleteId(null);
    setConfirmDialogOpen(false);
  };

  const handleAddDegreeProgram = () => {
    navigate("/Degree_Program-add");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredDegreePrograms = degreeProgramData.filter(
    (program) =>
      program &&
      ((program.title &&
        program.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (program.description &&
          program.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (program.program_name &&
          program.program_name.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Truncate text to a specific length
  const truncateText = (text, maxLength) => {
    if (!text) return "N/A";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  if (loading) {
    return (
      <LeftNavigationBar
        Content={
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="80vh"
          >
            <CircularProgress size={60} thickness={4} />
          </Box>
        }
      />
    );
  }

  return (
    <LeftNavigationBar
      Content={
        <Box sx={{ p: isMobile ? 1 : 3 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{
                position: "relative",
                padding: 0,
                margin: 0,
                fontWeight: 700,
                textAlign: "center",
                fontWeight: 300,
                fontSize: { xs: "32px", sm: "40px" },
                color: "#747474",
                textAlign: "center",
                textTransform: "uppercase",
                paddingBottom: "5px",
                mb: 3,
                mt: -4,
                "&::before": {
                  content: '""',
                  width: "28px",
                  height: "5px",
                  display: "block",
                  position: "absolute",
                  bottom: "3px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#747474",
                },
                "&::after": {
                  content: '""',
                  width: "100px",
                  height: "1px",
                  display: "block",
                  position: "relative",
                  marginTop: "5px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "#747474",
                },
              }}
            >
              Degree Program Panel
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Search programs..."
                  InputProps={{
                    startAdornment: (
                      <SearchIcon color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                  }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{ textAlign: { xs: "left", md: "right" } }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDegreeProgram}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  Add New Program
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{ backgroundColor: theme.palette.primary.main }}
                  >
                    <TableCell sx={{ color: "white", fontWeight: 600 }}>
                      Title
                    </TableCell>
                    {!isMobile && (
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        Description
                      </TableCell>
                    )}
                    <TableCell sx={{ color: "white", fontWeight: 600 }}>
                      Services & Info
                    </TableCell>
                    {!isMedium && (
                      <TableCell sx={{ color: "white", fontWeight: 600 }}>
                        Images
                      </TableCell>
                    )}
                    <TableCell sx={{ color: "white", fontWeight: 600 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDegreePrograms.length > 0 ? (
                    filteredDegreePrograms
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map(
                        (program) =>
                          program && (
                            <TableRow
                              key={program._id}
                              hover
                              sx={{
                                "&:nth-of-type(odd)": {
                                  backgroundColor: theme.palette.action.hover,
                                },
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell>
                                <Box sx={{ mb: 1 }}>
                                  <Typography fontWeight={500} variant="subtitle1">
                                    {program.program_name || program.title || "N/A"}
                                  </Typography>
                                  {program.slug && (
                                    <Chip 
                                      size="small" 
                                      label={program.slug} 
                                      sx={{ mt: 0.5 }}
                                      color="primary"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              {!isMobile && (
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {truncateText(program.description, 120) || "No description"}
                                  </Typography>
                                </TableCell>
                              )}
                              <TableCell>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  {/* Business Service */}
                                  {program.business_service && (
                                    <Chip 
                                      icon={<BusinessIcon />} 
                                      label={program.business_service.name || program.business_service.title} 
                                      size="small"
                                      sx={{ maxWidth: "100%", overflowX: "hidden" }}
                                      color="primary"
                                    />
                                  )}
                                  
                                  {/* Service */}
                                  {program.service && (
                                    <Chip 
                                      icon={<CategoryIcon />} 
                                      label={program.service.title} 
                                      size="small"
                                      sx={{ maxWidth: "100%", overflowX: "hidden" }}
                                      color="secondary"
                                    />
                                  )}
                                  
                                  {/* Company */}
                                  {program.company && (
                                    <Tooltip title={program.company.companyName}>
                                      <Chip 
                                        icon={<BusinessIcon />} 
                                        label={truncateText(program.company.companyName, 15)} 
                                        size="small"
                                        sx={{ maxWidth: "100%", overflowX: "hidden" }}
                                        color="success"
                                      />
                                    </Tooltip>
                                  )}
                                  
                                  {/* Colleges */}
                                  {program.college && program.college.length > 0 && (
                                    <Chip 
                                      icon={<SchoolIcon />} 
                                      label={`${program.college.length} College${program.college.length > 1 ? 's' : ''}`} 
                                      size="small"
                                      color="info"
                                    />
                                  )}
                                  
                                  {/* Year */}
                                  {program.year && (
                                    <Chip 
                                      label={`Year: ${program.year}`} 
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              {!isMedium && (
                                <TableCell>
                                  <Box
                                    sx={{ display: "flex", alignItems: "center" }}
                                  >
                                    <Badge
                                      badgeContent={program.images?.length || 0}
                                      color="primary"
                                      sx={{ mr: 1 }}
                                    >
                                      <Avatar
                                        src={program.images && program.images.length > 0 ? program.images[0] : undefined}
                                        sx={{
                                          width: 50,
                                          height: 50,
                                          backgroundColor:
                                            theme.palette.grey[300],
                                        }}
                                      >
                                        <ImageIcon />
                                      </Avatar>
                                    </Badge>
                                    <Typography variant="caption">
                                      {program.images?.length || 0} images
                                    </Typography>
                                  </Box>
                                </TableCell>
                              )}
                              <TableCell>
                                <Box sx={{ display: "flex", gap: 1 }}>
                                  <Tooltip title="View">
                                    <Button
                                      onClick={() => handleView(program._id)}
                                      color="info"
                                      size={isMobile ? "small" : "medium"}
                                      variant="outlined"
                                    >
                                      <ViewIcon />
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <Button
                                      onClick={() => handleEdit(program._id)}
                                      color="primary"
                                      size={isMobile ? "small" : "medium"}
                                      variant="outlined"
                                    >
                                      <EditIcon />
                                    </Button>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <Button
                                      onClick={() => handleDelete(program._id)}
                                      color="error"
                                      size={isMobile ? "small" : "medium"}
                                      variant="outlined"
                                    >
                                      <DeleteIcon />
                                    </Button>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )
                      )
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : (isMedium ? 4 : 5)}
                        align="center"
                        sx={{ py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <UploadIcon
                            color="disabled"
                            sx={{ fontSize: 60, mb: 1 }}
                          />
                          <Typography variant="h6" color="text.secondary">
                            No degree programs found
                          </Typography>
                          {searchTerm && (
                            <Typography variant="body2" color="text.secondary">
                              No results for "{searchTerm}"
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredDegreePrograms.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredDegreePrograms.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 2,
                    paddingRight: 1,
                  },
                }}
              />
            )}
          </Paper>

          <Dialog
            open={confirmDialogOpen}
            onClose={handleCloseDialog}
            PaperProps={{
              sx: {
                borderRadius: 2,
                minWidth: isMobile ? "90%" : 400,
              },
            }}
          >
            <DialogTitle
              sx={{
                backgroundColor: theme.palette.error.light,
                color: "white",
                fontWeight: 600,
              }}
            >
              Confirm Deletion
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
              <Typography variant="body1">
                Are you sure you want to delete this degree program? This action
                cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.grey[400],
                  color: theme.palette.text.primary,
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  "&:hover": {
                    backgroundColor: theme.palette.error.dark,
                  },
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      }
    />
  );
};

export default DegreeProgramControl;